// frontend/src/utils/licenseStates.js

// License state definitions for frontend
export const LICENSE_STATES = {
  ACTIVE: 'Active',
  EXTENSION_REQUEST: 'Extension Request',
  EXPIRED: 'Expired',
  NOT_KNOWN: 'Unknown',
  DISABLED: 'Disabled'
};

// State color mappings for UI
export const getStateColor = (status) => {
  const colors = {
    [LICENSE_STATES.ACTIVE]: '#28a745',      // Green
    [LICENSE_STATES.EXTENSION_REQUEST]: '#ffc107', // Yellow
    [LICENSE_STATES.EXPIRED]: '#dc3545',     // Red
    [LICENSE_STATES.NOT_KNOWN]: '#6c757d',   // Gray
    [LICENSE_STATES.DISABLED]: '#6c757d'     // Gray
  };
  return colors[status] || '#6c757d';
};

// State icon mappings for UI
export const getStateIcon = (status) => {
  const icons = {
    [LICENSE_STATES.ACTIVE]: 'pi-check-circle',
    [LICENSE_STATES.EXTENSION_REQUEST]: 'pi-clock',
    [LICENSE_STATES.EXPIRED]: 'pi-times-circle',
    [LICENSE_STATES.NOT_KNOWN]: 'pi-question-circle',
    [LICENSE_STATES.DISABLED]: 'pi-ban'
  };
  return icons[status] || 'pi-question-circle';
};

// Get state display information
export const getStateDisplayInfo = (license) => {
  const status = license.statusDisplay || license.status;
  return {
    status,
    color: license.statusColor || getStateColor(status),
    icon: license.statusIcon || getStateIcon(status),
    allowedTransitions: license.allowedTransitions || [],
    canExpire: license.canExpire || false
  };
};

// Check if user can perform action on license
export const canPerformAction = (license, action, userPermissions) => {
  if (!userPermissions) return false;

  switch (action) {
    case 'approveExtension':
      return userPermissions.licenseManagement?.approveExtensions &&
             license.allowedTransitions?.includes('ExtensionRequested');

    case 'requestExtension':
      return userPermissions.licenseManagement?.requestExtension;

    case 'enable':
      return userPermissions.basicInfraManagement?.activateUsers &&
             license.allowedTransitions?.includes('Disabled');

    case 'disable':
      return userPermissions.basicInfraManagement?.deactivateUsers &&
             license.allowedTransitions?.includes('Disabled');

    case 'syncLicenses':
      return userPermissions.licenseServerManagement?.syncLicenses;

    case 'rereadServer':
      return userPermissions.licenseServerManagement?.rereadServer;

    case 'restartLicenseServer':
      return userPermissions.licenseServerManagement?.restartLicenseServerService;

    default:
      return false;
  }
};

// Get available actions for license based on user permissions
export const getAvailableActions = (license, userPermissions) => {
  const actions = [];

  if (canPerformAction(license, 'approveExtension', userPermissions)) {
    actions.push({
      label: 'Approve Extension',
      action: 'approveExtension',
      icon: 'pi-check',
      severity: 'success'
    });
  }

  if (canPerformAction(license, 'requestExtension', userPermissions)) {
    actions.push({
      label: 'Request Extension',
      action: 'requestExtension',
      icon: 'pi-clock',
      severity: 'info'
    });
  }

  if (canPerformAction(license, 'enable', userPermissions)) {
    actions.push({
      label: 'Enable License',
      action: 'enable',
      icon: 'pi-play',
      severity: 'success'
    });
  }

  if (canPerformAction(license, 'disable', userPermissions)) {
    actions.push({
      label: 'Disable License',
      action: 'disable',
      icon: 'pi-pause',
      severity: 'warning'
    });
  }

  return actions;
};

// Format license data for display
export const formatLicenseForDisplay = (license) => {
  const stateInfo = getStateDisplayInfo(license);

  return {
    ...license,
    formattedStatus: stateInfo.status,
    statusColor: stateInfo.color,
    statusIcon: stateInfo.icon,
    canExpire: stateInfo.canExpire,
    expiryWarning: stateInfo.canExpire && license.endDate ?
      new Date(license.endDate) < new Date(Date.now() + 30 * 24 * 60 * 60 * 1000) : // 30 days
      false
  };
};
